import React from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography, Paper, Link, Divider } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const MarkdownContent = ({ content }) => {
    return (
        <Box
            className="ai-message-markdown"
            sx={{
                '& > *:first-child': { marginTop: 0 },
                '& > *:last-child': { marginBottom: 0 },
            }}
        >
            <ReactMarkdown
                children={content}
                remarkPlugins={[remarkGfm]}
                components={{
                    table: ({ node, ...props }) => (
                        <Paper
                            variant="outlined"
                            sx={{ my: 2, borderRadius: 2, overflowX: 'auto', border: '1px solid #E0E0E0' }}
                        >
                            <Table size="small" sx={{ minWidth: 650 }} {...props} />
                        </Paper>
                    ),
                    thead: ({ node, ...props }) => <TableHead sx={{ bgcolor: '#F5F7FA' }} {...props} />,
                    tbody: ({ node, ...props }) => <TableBody {...props} />,
                    tr: ({ node, ...props }) => (
                        <TableRow
                            sx={{
                                '&:last-child td, &:last-child th': { border: 0 },
                                '&:hover': { bgcolor: '#FAFAFA' },
                            }}
                            {...props}
                        />
                    ),
                    th: ({ node, ...props }) => (
                        <TableCell
                            sx={{
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                color: 'text.primary',
                                py: 1.5,
                                px: 2,
                                borderBottom: '1px solid #E0E0E0',
                            }}
                            {...props}
                        />
                    ),
                    td: ({ node, ...props }) => (
                        <TableCell
                            sx={{
                                fontSize: '0.875rem',
                                color: 'text.secondary',
                                py: 1.5,
                                px: 2,
                            }}
                            {...props}
                        />
                    ),
                    h1: ({ children }) => (
                        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mt: 3, mb: 2, color: '#1A1A1A' }}>
                            {children}
                        </Typography>
                    ),
                    h2: ({ children }) => (
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 3, mb: 2, color: '#1A1A1A' }}>
                            {children}
                        </Typography>
                    ),
                    h3: ({ children }) => (
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2, mb: 1, color: '#1A1A1A' }}>
                            {children}
                        </Typography>
                    ),
                    p: ({ children }) => (
                        <Typography
                            variant="body1"
                            paragraph
                            sx={{ lineHeight: 1.7, color: '#2C2C2C', fontSize: '0.95rem' }}
                        >
                            {children}
                        </Typography>
                    ),
                    ul: ({ children }) => (
                        <Box component="ul" sx={{ pl: 2, my: 1 }}>
                            {children}
                        </Box>
                    ),
                    ol: ({ children }) => (
                        <Box component="ol" sx={{ pl: 2, my: 1 }}>
                            {children}
                        </Box>
                    ),
                    li: ({ children }) => (
                        <Box
                            component="li"
                            sx={{ mb: 0.5, typography: 'body1', fontSize: '0.95rem', color: '#2C2C2C' }}
                        >
                            {children}
                        </Box>
                    ),
                    blockquote: ({ children }) => (
                        <Box
                            component="blockquote"
                            sx={{
                                borderLeft: '4px solid #B1C2F2',
                                m: 0,
                                pl: 2,
                                py: 1,
                                my: 2,
                                bgcolor: '#F8F9FA',
                                borderRadius: '0 4px 4px 0',
                                fontStyle: 'italic',
                            }}
                        >
                            {children}
                        </Box>
                    ),
                    code: ({ node, inline, className, children, ...props }) => {
                        const match = /language-(\w+)/.exec(className || '');
                        return !inline ? (
                            <Box
                                sx={{
                                    my: 2,
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    bgcolor: '#1E1E1E',
                                    color: '#D4D4D4',
                                    fontSize: '0.875rem',
                                    fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
                                }}
                            >
                                <Box
                                    sx={{
                                        px: 2,
                                        py: 1,
                                        borderBottom: '1px solid #333',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography variant="caption" sx={{ color: '#888' }}>
                                        {match ? match[1] : 'Code'}
                                    </Typography>
                                </Box>
                                <Box sx={{ p: 2, overflowX: 'auto' }}>
                                    <code className={className} {...props}>
                                        {children}
                                    </code>
                                </Box>
                            </Box>
                        ) : (
                            <Box
                                component="code"
                                sx={{
                                    bgcolor: '#F3F4F6',
                                    color: '#C7254E',
                                    px: 0.6,
                                    py: 0.2,
                                    borderRadius: 1,
                                    fontSize: '0.85em',
                                    fontFamily: 'Consolas, Monaco, monospace',
                                }}
                                {...props}
                            >
                                {children}
                            </Box>
                        );
                    },
                    a: ({ href, children }) => (
                        <Link
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            color="primary"
                            sx={{ fontWeight: 500 }}
                        >
                            {children}
                        </Link>
                    ),
                    hr: () => <Divider sx={{ my: 3 }} />,
                }}
            />
        </Box>
    );
};

export default MarkdownContent;
